import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import {
  Card,
  Input,
  Button,
  Typography,
  Space,
  message,
  Spin,
  FloatButton,
  Tabs,
} from "antd";
import {
  SoundOutlined,
  SendOutlined,
  CopyOutlined,
  DeleteOutlined,
  WifiOutlined,
  ReadOutlined,
  ArrowUpOutlined,
  PlayCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";

const { TextArea } = Input;
const { Title, Text } = Typography;

// --- Types ---
interface AudioResponse {
  audioContent: string;
}

// --- Data: Morse Character Map ---
const MORSE_CODE_MAP = [
  // Letters
  { char: "A", code: ".-" },
  { char: "B", code: "-..." },
  { char: "C", code: "-.-." },
  { char: "D", code: "-.." },
  { char: "E", code: "." },
  { char: "F", code: "..-." },
  { char: "G", code: "--." },
  { char: "H", code: "...." },
  { char: "I", code: ".." },
  { char: "J", code: ".---" },
  { char: "K", code: "-.-" },
  { char: "L", code: ".-.." },
  { char: "M", code: "--" },
  { char: "N", code: "-." },
  { char: "O", code: "---" },
  { char: "P", code: ".--." },
  { char: "Q", code: "--.-" },
  { char: "R", code: ".-." },
  { char: "S", code: "..." },
  { char: "T", code: "-" },
  { char: "U", code: "..-" },
  { char: "V", code: "...-" },
  { char: "W", code: ".--" },
  { char: "X", code: "-..-" },
  { char: "Y", code: "-.--" },
  { char: "Z", code: "--.." },
  // Numbers
  { char: "0", code: "-----" },
  { char: "1", code: ".----" },
  { char: "2", code: "..---" },
  { char: "3", code: "...--" },
  { char: "4", code: "....-" },
  { char: "5", code: "....." },
  { char: "6", code: "-...." },
  { char: "7", code: "--..." },
  { char: "8", code: "---.." },
  { char: "9", code: "----." },
  // Punctuation & Symbols
  { char: "!", code: "-.-.--" },
  { char: "@", code: ".--.-." },
  { char: "$", code: "...-..-" },
  { char: ".", code: ".-.-.-" },
  { char: ",", code: "--..--" },
  { char: "?", code: "..--.." },
  { char: "/", code: "-..-." },
  { char: "(", code: "-.--." },
  { char: ")", code: "-.--.-" },
  { char: "&", code: ".-..." },
  { char: ":", code: "---..." },
  { char: ";", code: "-.-.-." },
  { char: "=", code: "-...-" },
  { char: "+", code: ".-.-." },
  { char: "-", code: "-....-" },
  { char: "_", code: "..--.-" },
  { char: "'", code: ".----." },
  { char: '"', code: ".-..-." },
];

// --- Audio Constants ---
const DOT_DURATION = 100; // ms
const DASH_DURATION = DOT_DURATION * 3;
const SYMBOL_SPACE = DOT_DURATION; // Space between dots/dashes
const LETTER_SPACE = DOT_DURATION * 3; // Space between letters
const WORD_SPACE = DOT_DURATION * 7; // Space between words
const FREQUENCY = 600; // Hz

const MorseTranslator: React.FC = () => {
  const [input, setInput] = useState<string>("");
  const [output, setOutput] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [audioLoading, setAudioLoading] = useState<boolean>(false);
  const [isPlayingMorse, setIsPlayingMorse] = useState<boolean>(false);
  const [showHints, setShowHints] = useState<boolean>(false);

  // Track active tab: "text-to-morse" or "morse-to-text"
  const [activeTab, setActiveTab] = useState<string>("text-to-morse");

  // Refs for Web Audio API
  const audioContextRef = useRef<AudioContext | null>(null);
  const timeoutsRef = useRef<number[]>([]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopMorseSound();
    };
  }, []);

  // --- Logic: Local Translation ---

  // 1. Text -> Morse
  const translateToMorse = (text: string): string => {
    return text
      .toUpperCase()
      .split("")
      .map((char) => {
        if (char === " ") return "/";
        const found = MORSE_CODE_MAP.find((item) => item.char === char);
        return found ? found.code : "";
      })
      .filter((code) => code !== "")
      .join(" ");
  };

  // 2. Morse -> Text
  const translateToEnglish = (morse: string): string => {
    return morse
      .trim()
      .split(" ")
      .map((code) => {
        if (code === "/") return " ";
        const found = MORSE_CODE_MAP.find((item) => item.code === code);
        return found ? found.char : "?";
      })
      .join("");
  };

  const handleTranslate = () => {
    if (!input.trim()) {
      message.warning("Please enter content to translate.");
      return;
    }

    setLoading(true);
    // Simulate a brief processing delay for effect
    setTimeout(() => {
      let result = "";
      if (activeTab === "text-to-morse") {
        result = translateToMorse(input);
      } else {
        result = translateToEnglish(input);
      }
      setOutput(result);
      setLoading(false);
    }, 300);
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    stopMorseSound();
    setInput("");
    setOutput("");
  };

  // --- Audio Logic: Morse Beeper (Oscillator) ---

  const playTone = (ctx: AudioContext, duration: number, startTime: number) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = "sine";
    osc.frequency.value = FREQUENCY;

    osc.connect(gain);
    gain.connect(ctx.destination);

    // Smooth envelope to avoid clicking sounds
    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(1, startTime + 0.01);
    gain.gain.setValueAtTime(1, startTime + duration / 1000 - 0.01);
    gain.gain.linearRampToValueAtTime(0, startTime + duration / 1000);

    osc.start(startTime);
    osc.stop(startTime + duration / 1000);
  };

  const playMorseSound = (morseCode: string) => {
    // 1. Stop any existing playback (closes old context)
    stopMorseSound();

    // 2. Create NEW Audio Context for this session
    // We do NOT check for existing one, we always create fresh to ensure clean state
    const AudioContextClass =
      window.AudioContext || (window as any).webkitAudioContext;
    const newCtx = new AudioContextClass();
    audioContextRef.current = newCtx;

    setIsPlayingMorse(true);

    // 3. Parse Morse Code and Schedule Tones using the new Context
    let currentTime = newCtx.currentTime;
    const cleanCode = morseCode.trim();
    let totalDuration = 0;

    for (let i = 0; i < cleanCode.length; i++) {
      const char = cleanCode[i];

      if (char === ".") {
        playTone(newCtx, DOT_DURATION, currentTime);
        currentTime += DOT_DURATION / 1000;
        currentTime += SYMBOL_SPACE / 1000;
        totalDuration += DOT_DURATION + SYMBOL_SPACE;
      } else if (char === "-" || char === "_") {
        playTone(newCtx, DASH_DURATION, currentTime);
        currentTime += DASH_DURATION / 1000;
        currentTime += SYMBOL_SPACE / 1000;
        totalDuration += DASH_DURATION + SYMBOL_SPACE;
      } else if (char === " ") {
        // Space between letters
        currentTime += (LETTER_SPACE - SYMBOL_SPACE) / 1000;
        totalDuration += LETTER_SPACE - SYMBOL_SPACE;
      } else if (char === "/") {
        // Space between words
        currentTime += (WORD_SPACE - SYMBOL_SPACE) / 1000;
        totalDuration += WORD_SPACE - SYMBOL_SPACE;
      }
    }

    // 4. Schedule state reset after playback finishes
    const timeoutId = window.setTimeout(() => {
      setIsPlayingMorse(false);
      // Optional: Close context after playback to free resources
      // stopMorseSound(); // Can be called here if auto-cleanup is desired
    }, totalDuration + 100);

    timeoutsRef.current.push(timeoutId);
  };

  const stopMorseSound = () => {
    // 1. Clear UI Reset Timeouts
    timeoutsRef.current.forEach((id) => clearTimeout(id));
    timeoutsRef.current = [];

    // 2. Close Audio Context (stops all scheduled sounds immediately)
    if (audioContextRef.current) {
      // We don't wait for the promise; we assume it's closing and discard the ref.
      audioContextRef.current.close().catch((err) => console.error(err));
      audioContextRef.current = null;
    }

    setIsPlayingMorse(false);
  };

  // --- API Handler: Text to Speech (English) ---
  const handleSpeakEnglish = async (textToSpeak: string) => {
    if (!textToSpeak.trim()) return;

    setAudioLoading(true);

    try {
      const response = await axios.get<AudioResponse>(
        "https://googlespeak.netlify.app/api/tts",
        {
          params: { text: textToSpeak, lang: "en" },
        },
      );

      const audioContent = response.data.audioContent;

      if (audioContent) {
        const audioSrc = audioContent.startsWith("data:")
          ? audioContent
          : `data:audio/mp3;base64,${audioContent}`;

        const audio = new Audio(audioSrc);
        await audio.play();
      } else {
        message.error("Audio signal not received.");
      }
    } catch (error) {
      console.error("TTS Error:", error);
      message.error("Failed to generate audio.");
    } finally {
      setAudioLoading(false);
    }
  };

  // --- UI Helpers ---
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success("Copied to clipboard!");
  };

  // Styles
  const cardStyle: React.CSSProperties = {
    borderRadius: "24px",
    boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
    border: "none",
    background: "#ffffff",
    overflow: "hidden",
  };

  const terminalStyle: React.CSSProperties = {
    background: "#2b2b2b",
    color: "#4ade80",
    padding: "20px",
    borderRadius: "12px",
    minHeight: "100px",
    position: "relative",
    boxShadow: "inset 0 2px 10px rgba(0,0,0,0.5)",
  };

  const isTextToMorse = activeTab === "text-to-morse";
  const inputPlaceholder = isTextToMorse
    ? "Type your message here (e.g. Hello World)..."
    : "Enter dots and dashes (e.g. .... . .-.. .-.. ---)...";
  const outputLabel = isTextToMorse ? "Morse Output" : "English Output";
  const inputLabel = isTextToMorse ? "Input English Text" : "Input Morse Code";

  return (
    <div style={{ maxWidth: 700, margin: "60px auto", padding: "0 20px" }}>
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "40px" }}>
        <WifiOutlined
          className="floating-icon"
          style={{ fontSize: "48px", color: "#fa8c16", marginBottom: "16px" }}
        />
        <Title
          level={1}
          style={{ margin: 0, fontWeight: 700, color: "#2c3e50" }}
        >
          Morse &middot; Code
        </Title>
        <Text style={{ fontSize: "16px", color: "#8c8c8c" }}>
          Universal Signal Translator
        </Text>
      </div>

      {/* Main Translator Card */}
      <Card bodyStyle={{ padding: "0 40px 40px 40px" }} style={cardStyle}>
        {/* Navigation Tabs */}
        <Tabs
          defaultActiveKey="text-to-morse"
          centered
          size="large"
          onChange={handleTabChange}
          style={{ marginBottom: "20px" }}
          items={[
            { label: "Text → Morse", key: "text-to-morse" },
            { label: "Morse → Text", key: "morse-to-text" },
          ]}
        />

        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {/* INPUT AREA */}
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "8px",
              }}
            >
              <Text
                strong
                style={{
                  fontSize: "14px",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  color: "#fa8c16",
                }}
              >
                {inputLabel}
              </Text>
              <Space>
                {/* MODE: Morse->Text | Allow playing Input (Morse) */}
                {!isTextToMorse && input && (
                  <Button
                    type="text"
                    size="small"
                    shape="round"
                    icon={
                      isPlayingMorse ? <StopOutlined /> : <PlayCircleOutlined />
                    }
                    onClick={() =>
                      isPlayingMorse ? stopMorseSound() : playMorseSound(input)
                    }
                    danger={isPlayingMorse}
                  >
                    {isPlayingMorse ? "Stop Signal" : "Play Signal"}
                  </Button>
                )}

                {/* MODE: Text->Morse | Allow reading Input (English) */}
                {isTextToMorse && input && (
                  <Button
                    type="text"
                    size="small"
                    shape="round"
                    icon={<SoundOutlined />}
                    loading={audioLoading}
                    onClick={() => handleSpeakEnglish(input)}
                  >
                    Read Aloud
                  </Button>
                )}

                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    stopMorseSound();
                    setInput("");
                    setOutput("");
                  }}
                  disabled={!input}
                >
                  Clear
                </Button>
              </Space>
            </div>

            <TextArea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={inputPlaceholder}
              rows={4}
              style={{
                borderRadius: "12px",
                border: "2px solid #f0f0f0",
                padding: "16px",
                fontSize: "18px",
                background: "#fafafa",
                fontFamily: !isTextToMorse
                  ? "'Fira Code', monospace"
                  : "inherit",
              }}
              bordered={false}
            />
          </div>

          {/* Action Button */}
          <Button
            type="primary"
            block
            size="large"
            icon={<SendOutlined />}
            onClick={handleTranslate}
            loading={loading}
            style={{
              height: "56px",
              borderRadius: "12px",
              fontSize: "18px",
              fontWeight: 600,
              marginTop: "10px",
            }}
          >
            {loading ? "Translating..." : "Translate Signal"}
          </Button>

          {/* OUTPUT AREA */}
          {(output || loading) && (
            <div style={{ marginTop: "20px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                  padding: "0 4px",
                }}
              >
                <Text
                  strong
                  style={{
                    fontSize: "14px",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    color: "#fa8c16",
                  }}
                >
                  {outputLabel}
                </Text>
                <Space>
                  {/* MODE: Text->Morse | Allow playing Output (Morse) */}
                  {isTextToMorse && output && (
                    <Button
                      type="text"
                      size="small"
                      icon={
                        isPlayingMorse ? (
                          <StopOutlined />
                        ) : (
                          <PlayCircleOutlined />
                        )
                      }
                      onClick={() =>
                        isPlayingMorse
                          ? stopMorseSound()
                          : playMorseSound(output)
                      }
                      danger={isPlayingMorse}
                    >
                      {isPlayingMorse ? "Stop Signal" : "Play Signal"}
                    </Button>
                  )}

                  {/* MODE: Morse->Text | Allow reading Output (English) */}
                  {!isTextToMorse && output && (
                    <Button
                      type="text"
                      size="small"
                      icon={<SoundOutlined />}
                      loading={audioLoading}
                      onClick={() => handleSpeakEnglish(output)}
                    >
                      Listen
                    </Button>
                  )}

                  <Button
                    size="small"
                    type="dashed"
                    shape="round"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(output)}
                    disabled={!output}
                  >
                    Copy
                  </Button>
                </Space>
              </div>

              <div style={terminalStyle}>
                {loading ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                      minHeight: "60px",
                    }}
                  >
                    <Spin style={{ color: "#4ade80" }} tip="Decoding..." />
                  </div>
                ) : (
                  <div
                    className="morse-terminal"
                    style={{
                      fontSize: "24px",
                      lineHeight: "1.6",
                      wordBreak: "break-word",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {output}
                  </div>
                )}
              </div>
            </div>
          )}
        </Space>
      </Card>

      {/* Alphabet Reference Section */}
      <div style={{ marginTop: "30px", textAlign: "center" }}>
        <Button
          type="text"
          icon={<ReadOutlined />}
          onClick={() => setShowHints(!showHints)}
          style={{ color: "#8c8c8c", fontSize: "14px" }}
        >
          {showHints ? "Hide Reference Guide" : "Show Signal Reference Guide"}
        </Button>

        {showHints && (
          <div className="morse-grid">
            {MORSE_CODE_MAP.map((item) => (
              <div key={item.char} className="morse-key">
                <span className="key-char">{item.char}</span>
                <span className="key-code">{item.code}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ textAlign: "center", marginTop: "40px", opacity: 0.6 }}>
        <Text style={{ fontSize: "12px" }}>
          &copy; {new Date().getFullYear()} Morse/AI Translator System
        </Text>
      </div>

      <FloatButton.BackTop
        type="primary"
        icon={<ArrowUpOutlined />}
        style={{ right: 34, bottom: 34, backgroundColor: "#fa8c16" }}
        visibilityHeight={200}
      />
    </div>
  );
};

export default MorseTranslator;
